// API client for Assessment videos (/api/assessment/admin/videos).
//
// Backend contract:
//   - responses are { data, isError, statusCode, errorMessage }
//   - GET returns a page in `data`: { content, page, size, totalElements, totalPages, hasNext }
//     with rows { videoId, topicId, topicName, subjectIds, subjectNames, title, videoPath,
//     durationSeconds, category, recommended, displayOrder, active }
//   - POST/PUT take { topicId, title, videoPath, durationSeconds, category, recommended,
//     displayOrder, active }; PUT is partial (null fields are left unchanged)
//   - DELETE is a hard delete
//   - the file itself goes up first via POST /api/ftp/uploadAssessmentVideo (multipart `file` +
//     `topicId`, 200 MB default cap) which returns { data: { path } }; that path is then sent as
//     `videoPath`.
import api from "../axiosInstance";

const BASE = "/api/assessment/admin/videos";

const unwrap = (res) => res.data?.data;

export const normalizeVideo = (v) => ({
  ...v,
  id: v.videoId ?? v.id,
  subjectIds: v.subjectIds ?? [],
  subjectNames: v.subjectNames ?? [],
  recommended: Boolean(v.recommended),
  active: Boolean(v.active),
});

// filters: topicId, subjectId, isActive, recommended, search, page, size
export async function listVideos({ page = 0, size = 10, ...filters } = {}) {
  const params = Object.fromEntries(
    Object.entries({ ...filters, page, size }).filter(([, v]) => v !== undefined && v !== null && v !== ""),
  );
  const res = await api.get(BASE, { params });
  const data = unwrap(res) || {};
  return { ...data, content: (data.content || []).map(normalizeVideo) };
}

export const createVideo = (payload) => api.post(BASE, payload).then(unwrap);
export const updateVideo = (id, payload) => api.put(`${BASE}/${id}`, payload).then(unwrap);
export const deleteVideo = (id) => api.delete(`${BASE}/${id}`).then(unwrap);

// Uploads the file to the topic's folder and resolves with the stored path.
export async function uploadVideoFile(file, topicId, onProgress) {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("topicId", topicId);
  const res = await api.post("/api/ftp/uploadAssessmentVideo", formData, {
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
  if (res.data?.isError) throw new Error(res.data.errorMessage || "Video upload failed");
  return unwrap(res)?.path;
}

// Stored paths look like "/uploads/assessments/videos/{topicId}/x.mp4"; the old helper in
// assessmentVideosData.js would build "public//uploads/...", so strip the leading slash here.
export const resolveMediaUrl = (path) =>
  !path
    ? ""
    : /^https?:\/\//.test(path)
      ? path
      : `https://siaedutech.com/public/${path.replace(/^\/+/, "")}`;

// Reads a video file's length in whole seconds from its metadata (undefined if unreadable).
export const readVideoDurationSeconds = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const el = document.createElement("video");
    const done = (value) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    el.preload = "metadata";
    el.onloadedmetadata = () => done(Number.isFinite(el.duration) ? Math.round(el.duration) : undefined);
    el.onerror = () => done(undefined);
    el.src = url;
  });

export const formatDuration = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return "—";
  const s = Math.max(0, Math.round(Number(seconds)));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
};
